import { MutableRefObject, useMemo, useRef } from 'react';

import { MESSAGE_ROLE } from '../../../components/Message/types/messages';
import { TranscriptContextValue } from '../../../store/transcript/store';

const GUID_REGEX = new RegExp(/^[a-f\d]{8}(-[a-f\d]{4}){3}-[a-f\d]{12}$/i);

const isValidSessionValue = (sessionKey: string | null | undefined): boolean => {
    return !!sessionKey && GUID_REGEX.test(sessionKey);
};

const loadItem = (persistanceKey: string): string | null => {
    return window.localStorage.getItem(persistanceKey) || null;
};

const saveItem = (persistanceKey: string, value: string | null | undefined): void => {
    window.localStorage.setItem(persistanceKey, value || '');
};

// localStorage utility functions (exported for use in other session management modules)
export const deleteItem = (persistanceKey: string): void => {
    window.localStorage.removeItem(persistanceKey);
};

const saveSessionKey = (persistanceKey: string, sessionKey: string | null | undefined): void => {
    if (isValidSessionValue(sessionKey)) {
        saveItem(persistanceKey, sessionKey);
    }
};

export const loadSessionKey = (persistanceKey: string, lastActivityPersistanceKey: string): string | null => {
    const sessionKey = loadItem(persistanceKey);
    if (isValidSessionValue(sessionKey)) {
        return sessionKey;
    }

    deleteItem(persistanceKey);
    deleteItem(lastActivityPersistanceKey);
    return null;
};

export const saveLastActivityTime = (lastActivityPersistanceKey: string) => {
    saveItem(lastActivityPersistanceKey, Date.now().toString());
};

const loadLastActivityTime = (lastActivityPersistanceKey: string): number => {
    return parseInt(loadItem(lastActivityPersistanceKey) ?? '');
};

const isUserIdle = (lastActivityPersistanceKey: string, currentMessageCount: number): boolean => {
    const lastActivityTime = loadLastActivityTime(lastActivityPersistanceKey);
    const isIdle = !isNaN(lastActivityTime) && Date.now() - lastActivityTime >= 3600000; // 1 Hour
    return isIdle && currentMessageCount > 0;
};

const resetSession = (lastActivityPersistanceKey: string, newTranscriptContext: TranscriptContextValue) => {
    newTranscriptContext.resetSession?.();
    deleteItem(lastActivityPersistanceKey);
};

// Being "active" is defined by user's last input
const trackIfActive = (
    lastActivityPersistanceKey: string,
    currentMessageCount: number,
    newTranscriptContext: TranscriptContextValue
) => {
    const newMessageCount = newTranscriptContext.state.messages.length;
    const latestMessageRole = newTranscriptContext.state.messages[newMessageCount - 1]?.role ?? '';

    if (newMessageCount > currentMessageCount && latestMessageRole === MESSAGE_ROLE.USER) {
        saveLastActivityTime(lastActivityPersistanceKey);
    }
};

export const usePersistSession = (
    sessionPersistanceKey: string,
    lastActivityPersistanceKey: string,
    onNewSessionCreated?: (sessionId: string) => void,
    disablePersistence?: boolean
): MutableRefObject<TranscriptContextValue | null> => {
    const _chatContextRef = useRef<TranscriptContextValue | null>(null);

    // Initialize with existing session from localStorage (if any)
    const initialSessionId = disablePersistence ? null : loadItem(sessionPersistanceKey);
    const previousSessionIdRef = useRef<string | null>(initialSessionId);

    // Keep mutable refs so the Proxy trap always reads the latest values
    // without requiring the Proxy itself to be recreated.
    const sessionKeyRef = useRef(sessionPersistanceKey);
    sessionKeyRef.current = sessionPersistanceKey;
    const activityKeyRef = useRef(lastActivityPersistanceKey);
    activityKeyRef.current = lastActivityPersistanceKey;
    const onNewSessionRef = useRef(onNewSessionCreated);
    onNewSessionRef.current = onNewSessionCreated;
    const disableRef = useRef(disablePersistence);
    disableRef.current = disablePersistence;

    const chatContextRef = useMemo(() => {
        return new Proxy<MutableRefObject<TranscriptContextValue | null>>(_chatContextRef, {
            set(_ref, prop, newVal: TranscriptContextValue | null) {
                if (prop === 'current') {
                    if (newVal) {
                        const sKey = sessionKeyRef.current;
                        const aKey = activityKeyRef.current;
                        const newSessionId = newVal?.state.sessionId;
                        const previousSessionId = previousSessionIdRef.current;
                        const currentMessageCount = _ref.current?.state.messages.length ?? 0;

                        if (newVal.state.loadFailed || disableRef.current) {
                            deleteItem(sKey);
                            deleteItem(aKey);
                        } else if (
                            isUserIdle(aKey, currentMessageCount) ||
                            (newSessionId && !isValidSessionValue(newSessionId))
                        ) {
                            resetSession(aKey, newVal);
                            saveSessionKey(sKey, newSessionId);
                        } else {
                            trackIfActive(aKey, currentMessageCount, newVal);
                            saveSessionKey(sKey, newSessionId);
                        }

                        // Notify callback when a brand new session is created
                        if (newSessionId && newSessionId !== previousSessionId &&
                            (isValidSessionValue(newSessionId) || (newSessionId === '' && newVal.state.isAwaitingServerMessage))) {
                            if (!previousSessionId || currentMessageCount === 0) {
                                if (newSessionId !== '') {
                                    previousSessionIdRef.current = newSessionId;
                                }
                                onNewSessionRef.current?.(newSessionId);
                            } else {
                                previousSessionIdRef.current = newSessionId;
                            }
                        }
                    }
                    _ref[prop] = newVal;
                }
                return true;
            }
        });
    }, []);

    return chatContextRef;
};
